import type { ReactNode } from 'react';

import { cn } from '@/lib/utils';

import { formatCount } from './formatCount';
import { SECTION_HEADING } from './sectionHeading';

export interface SectionHeadingProps {
  /** `h2` on a tab (under the page's `h1`); `h3` for a card under another `h2`. */
  as?: 'h2' | 'h3';
  /** For a card's `aria-labelledby`, or a link that scrolls here. */
  id?: string;
  /** Drawn after a dot, muted and tabular: "Members · 12". */
  count?: number;
  /** Trailing content at the far right of the same line. */
  action?: ReactNode;
  children: string;
  className?: string;
}

/**
 * The heading above a group of things (spec 226 FR-014): the app's uppercase
 * section label as a real heading element, so it appears in the page outline,
 * with an optional count and an optional action on the same line.
 *
 * The classes are exactly `SECTION_HEADING` from `sectionHeading.ts` — this
 * component never adds a size or a case. The count sits inside the heading so
 * the accessible name is "Members · 12"; without a count no dot is drawn.
 */
export function SectionHeading({
  as: Tag = 'h2',
  id,
  count,
  action,
  children,
  className,
}: SectionHeadingProps) {
  return (
    <div className={cn('flex items-center justify-between gap-2', className)}>
      <Tag id={id} className={SECTION_HEADING}>
        {children}
        {count !== undefined ? (
          <span className="font-normal tabular-nums">
            {' · '}
            {formatCount(count)}
          </span>
        ) : null}
      </Tag>
      {action}
    </div>
  );
}
